import dayjs from "dayjs";

/**
 * Validates Czech or Slovak birth number format and calculates birthday
 * Format: YYMMDD/XXXX or YYMMDDXXXX (10 digits for people born 1.1.1954+)
 * Format: YYMMDDXXX (9 digits for people born before 1.1.1954)
 * For people born 1.1.1954+: must pass modulo 11 check
 * Validates that birth number format matches the sex (women have +50 to month)
 *
 * @param {string} birthNumber - Birth number to validate
 * @param {string} sex - Sex ("male" or "female")
 * @returns {{ valid: boolean, error?: string, birthday?: dayjs.Dayjs }}
 */
export function validateBirthNumber(birthNumber, sex) {
  if (!birthNumber) {
    return { valid: false, error: "Rodné číslo je povinné" };
  }

  // Remove spaces and normalize format
  const normalized = birthNumber.replace(/\s/g, "").replace(/\//g, "");

  // Check that all characters are digits first
  if (!/^\d+$/.test(normalized)) {
    return { valid: false, error: "Rodné číslo musí obsahovat pouze číslice" };
  }

  // Extract year from first two digits to determine expected format
  const year = parseInt(normalized.substring(0, 2), 10);
  const thisYear = parseInt(dayjs().year().toString().slice(-2), 10);
  const fullYear = year <= thisYear ? 2000 + year : 1900 + year;

  // Determine expected format based on birth year
  // People born before 1954 should have 9 digits, others should have 10 digits
  const expectedLength = fullYear < 1954 ? 9 : 10;

  // Check format matches expected length
  if (normalized.length !== expectedLength) {
    if (expectedLength === 9) {
      return { valid: false, error: "Rodné číslo pro ročníky před rokem 1954 musí obsahovat 9 číslic" };
    } else {
      return { valid: false, error: "Rodné číslo musí obsahovat 10 číslic" };
    }
  }

  const month = parseInt(normalized.substring(2, 4), 10);
  const day = parseInt(normalized.substring(4, 6), 10);

  // Check month validity (01-12 for men, 51-62 for women, or 21-32, 71-82 for historical)
  // Handle women's month offset
  let actualMonth = month;
  let isFemale = false;
  if (month > 70) {
    actualMonth = month - 70;
    isFemale = true;
  } else if (month > 50) {
    actualMonth = month - 50;
    isFemale = true;
  } else if (month > 20) {
    actualMonth = month - 20;
    isFemale = true;
  }

  if (actualMonth < 1 || actualMonth > 12) {
    return { valid: false, error: "Neplatný měsíc v rodném čísle" };
  }

  // Validate that birth number format matches the selected sex
  if (sex) {
    const isFemaleFormat = isFemale;
    const expectedFemale = sex === "female";
    if (isFemaleFormat !== expectedFemale) {
      return {
        valid: false,
        error: expectedFemale
          ? "Rodné číslo neodpovídá pohlaví (ženy mají v rodném čísle měsíc +50)"
          : "Rodné číslo neodpovídá pohlaví (muži mají v rodném čísle měsíc bez +50)",
      };
    }
  }

  // Check day validity (basic check, full validation would need to check month days)
  if (day < 1 || day > 31) {
    return { valid: false, error: "Neplatný den v rodném čísle" };
  }

  // Modulo 11 check only for people born 1.1.1954+ (10-digit format)
  // People born before 1954 have 9-digit format and don't need modulo 11 check
  if (normalized.length === 10) {
    if (fullYear > 1953 || (fullYear === 1953 && actualMonth === 1 && day >= 1)) {
      const fullNumber = parseInt(normalized, 10);
      if (fullNumber % 11 !== 0) {
        return { valid: false, error: "Rodné číslo neprošlo kontrolou modulo 11" };
      }
    }
  }

  // Calculate birthday from validated birth number
  try {
    const date = dayjs(`${fullYear}-${String(actualMonth).padStart(2, "0")}-${String(day).padStart(2, "0")}`);
    if (date.isValid()) {
      return { valid: true, birthday: date };
    }
  } catch {
    // Invalid date
  }

  return { valid: true };
}
